import { UrlService } from './../services/url.service';
import { HttpClient } from '@angular/common/http';
import { ILoginInformation } from './../models/login-information';
import { IOrganization } from './../models/organization';
import { IMember, IMemberResult } from './../models/member';
import { StateContext } from '@ngxs/store';
import { Action, State, Selector } from '@ngxs/store';
import { PasswordResult, UserStateModel } from './user.model';
import { Injectable } from '@angular/core';
import { User } from './user.actions';
import {
  debounceTime,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Observable, Subject, Subscription } from 'rxjs';
import { UserService } from '../services/user/user.service';

@State<UserStateModel>({
  name: 'user',
  defaults: {
    currentOrganization: undefined,
    isLoading: false,
    currentUser: undefined,
    organizations: undefined,
    loginInformation: undefined,
    isPasswordUpdated: false,
    updatePasswordResult: undefined,
  },
})
@Injectable()
export class UserState {
  unSubscribeAll$ = new Subject<void>();
  constructor(
    private http: HttpClient,
    private urlService: UrlService,
    private userService: UserService
  ) {}

  @Selector()
  public static getCurrentUser(state: UserStateModel): IMember {
    return state.currentUser;
  }

  @Selector()
  public static getCurrentOrganization(state: UserStateModel): IOrganization {
    return state.currentOrganization;
  }

  @Selector()
  static getScannedTicket(state: UserStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  public static getLoginInformation(state: UserStateModel): ILoginInformation {
    return {
      currentUser: state.currentUser,
      currentOrganization: state.currentOrganization,
      organizations: state.organizations,
    } as ILoginInformation;
  }

  @Selector()
  public static getUserOrganizations(
    state: UserStateModel
  ): Array<IOrganization> {
    return state.organizations;
  }

  @Selector()
  public static getLoadingStatus(state: UserStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  public static getPasswordUpdatedStatus(
    state: UserStateModel
  ): PasswordResult {
    return state.updatePasswordResult;
  }
  @Action(User.Actions.SetCurrentUser)
  public setCurrentUser(
    ctx: StateContext<UserStateModel>,
    action: User.Actions.SetCurrentUser
  ) {
    ctx.patchState({ isLoading: true });

    action.user.id = action.user['_id'];
    const loginInformation = !!ctx.getState().loginInformation
      ? ctx.getState().loginInformation
      : ({
          currentOrganization: undefined,
          currentUser: undefined,
          organizations: [],
        } as ILoginInformation);

    let organization = loginInformation.currentOrganization;
    if (organization && !organization.id) {
      organization.id = organization['_id'];
    }

    const newLoginInformation = {
      currentOrganization: organization,
      currentUser: action.user,
      organizations: loginInformation.organizations,
    } as ILoginInformation;

    action.user.id = action.user['_id'];
    ctx.patchState({
      currentOrganization: organization,
      currentUser: action.user,
      loginInformation: newLoginInformation,
      organizations: loginInformation.organizations,
      isLoading: false,
    });
  }

  @Action(User.Actions.SetCurrentOrganization)
  public setCurrentOrganization(
    { patchState, getState }: StateContext<UserStateModel>,
    action: User.Actions.SetCurrentOrganization
  ) {
    patchState({ isLoading: true });

    const loginInformation = !!getState().loginInformation
      ? getState().loginInformation
      : ({} as ILoginInformation);

    action.organization.id = action.organization['_id'];
    loginInformation.currentUser.id = loginInformation.currentUser['_id'];

    const newLoginInformation = {
      currentOrganization: action.organization,
      currentUser: loginInformation.currentUser,
      organizations: loginInformation.organizations,
    } as ILoginInformation;

    patchState({
      currentOrganization: action.organization,
      loginInformation: newLoginInformation,
      isLoading: false,
    });
  }

  @Action(User.Actions.UpdatePassword)
  public updatePassword(
    { patchState, getState, dispatch }: StateContext<UserStateModel>,
    action: User.Actions.UpdatePassword
  ): Subscription {
    patchState({ isLoading: true });

    return this.userService
      .updatePassword(action.value)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((value) => {
        dispatch(new User.Actions.UpdatePasswordResult(value));
        patchState({
          isLoading: false,
          // updatePasswordResult: value;
        });
      });
  }

  @Action(User.Actions.UpdatePasswordResult)
  public updatePasswordResult(
    { patchState, getState }: StateContext<UserStateModel>,
    action: User.Actions.UpdatePasswordResult
  ): void {
    patchState({ updatePasswordResult: action.result });
  }

  @Action(User.Actions.UpdateUsername)
  public updateUsername(
    { patchState, getState, dispatch }: StateContext<UserStateModel>,
    action: User.Actions.UpdateUsername
  ): Subscription {
    patchState({ isLoading: true });

    return this.userService
      .updateUsername(action.value)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((value) => {
        dispatch(new User.Actions.UpdatePasswordResult(value));
        patchState({
          isLoading: false,
        });
      });
  }
  private loginInfoCache = new Map<string, Observable<IMemberResult>>();
  @Action(User.Actions.GetLoginInformation)
  public getLoginInformation(
    ctx: StateContext<UserStateModel>,
    action: User.Actions.GetLoginInformation
  ) {
    const email = action.email;
    ctx.patchState({ isLoading: true });

    // Return cached observable if it exists
    if (this.loginInfoCache.has(email)) {
      return this.loginInfoCache
        .get(email)!
        .pipe(
          tap((userResponse) => this.handleLoginResponse(ctx, userResponse))
        );
    }

    const url = `${this.urlService.buildUrl(
      'users',
      'getLoginInformation'
    )}/${email}`;

    const request$ = this.http.get<IMemberResult>(url).pipe(
      take(1), // ensures only one emission
      shareReplay(1) // cache and share
    );

    this.loginInfoCache.set(email, request$);

    return request$.pipe(
      tap((userResponse) => this.handleLoginResponse(ctx, userResponse))
    );
  }

  private handleLoginResponse(
    ctx: StateContext<UserStateModel>,
    userResponse: IMemberResult
  ) {
    const loginInformation: ILoginInformation = {} as ILoginInformation;

    let organization: IOrganization | undefined = undefined;

    if (userResponse.organizations?.length) {
      organization = userResponse.organizations[0];
      organization.id = organization.organizationId; // TODO: make this dynamic
    }

    loginInformation.currentOrganization = organization;
    loginInformation.currentUser = userResponse;
    loginInformation.currentUser.id = userResponse['_id'];
    loginInformation.organizations = userResponse.organizations;

    if (organization?.attributes) {
      loginInformation.currentUser.attributes = organization.attributes;
    }

    ctx.patchState({
      organizations: loginInformation.organizations,
      currentOrganization: loginInformation.currentOrganization,
      currentUser: loginInformation.currentUser,
      loginInformation,
      isLoading: false,
    });
  }

  // @Action(User.Actions.GetLoginInformation)
  // public getLoginInformation(
  //   ctx: StateContext<UserStateModel>,
  //   action: User.Actions.GetLoginInformation
  // ) {
  //   ctx.patchState({ isLoading: true });

  //   const url = `${this.urlService.buildUrl('users', 'getLoginInformation')}/${
  //     action.email
  //   }`;

  //   return this.http
  //     .get<IMemberResult>(`${url}`)
  //     .pipe(debounceTime(2000))
  //     .subscribe((userResponse) => {
  //       const loginInformation: ILoginInformation = (<
  //         ILoginInformation
  //       >{}) as ILoginInformation;

  //       let organization = undefined;
  //       //1. Set Current Organization
  //       if (
  //         userResponse.organizations &&
  //         userResponse.organizations.length > 0
  //       ) {
  //         organization = userResponse.organizations[0] as IOrganization;
  //       }
  //       // TODO: Change when this becomes dynamic
  //       if (organization) {
  //         organization.id = organization.organizationId;
  //       }

  //       //2. Set Current User
  //       loginInformation.currentOrganization = organization;
  //       loginInformation.currentUser = userResponse;

  //       loginInformation.currentUser.id = userResponse['_id'];
  //       loginInformation.organizations = userResponse.organizations;
  //       //Place User Org Attributes on root user
  //       if (organization && organization.attributes) {
  //         loginInformation.currentUser.attributes = organization.attributes;
  //       }
  //       //return the different  current Items
  //       ctx.patchState({
  //         organizations: loginInformation.organizations,
  //         currentOrganization: loginInformation.currentOrganization,
  //         currentUser: loginInformation.currentUser,
  //         loginInformation: loginInformation,
  //         isLoading: false,
  //       });
  //     });
  // }

  @Action(User.Actions.SetLoginInformation)
  public setLoginInformation(
    ctx: StateContext<UserStateModel>,
    action: User.Actions.SetLoginInformation
  ) {
    ctx.patchState({ isLoading: true });

    const loginInformation: ILoginInformation = (<
      ILoginInformation
    >{}) as ILoginInformation;

    //1. Set Current Organization
    let organization = undefined;

    if (action.memberOrganization.currentOrganization) {
      organization = action.memberOrganization.currentOrganization;
    }

    if (!organization) {
      const organizations = action.memberOrganization.organizations;
      organization = organizations[0];
      if (
        action.memberOrganization &&
        action.memberOrganization.preferences &&
        action.memberOrganization.preferences.defaultOrganization
      ) {
        const defaultOrganization =
          action.memberOrganization.preferences.defaultOrganization;
        const org = organizations.find((org) => org.id === defaultOrganization);
        organization = org;
      }
    }

    let orgId = '';
    let organizationBase = undefined;

    if (organization && organization.organization) {
      organizationBase = organization.organization;
      if (organizationBase && organizationBase.id) {
        orgId = organizationBase['_id'];
      }
    }

    //2. Set Current User
    loginInformation.currentOrganization = { ...organization, id: orgId };
    loginInformation.currentUser = action.memberOrganization;

    if (
      action.memberOrganization &&
      action.memberOrganization['_id'] &&
      action.memberOrganization.organizations
    ) {
      loginInformation.currentUser.id = action.memberOrganization['_id'];
      loginInformation.organizations = action.memberOrganization.organizations;
    }

    //Place User Org Attributes on root user
    if (organization && organization.attributes) {
      loginInformation.currentUser.attributes = organization.attributes;
    }
    //return the different  current Items
    ctx.patchState({
      currentOrganization: loginInformation.currentOrganization,
      currentUser: loginInformation.currentUser,
      loginInformation: loginInformation,
      isLoading: false,
    });
  }
}
